// src/components/tasks/TaskKanban.js
// ✅ DROP-IN: Modern kanban board (no bulky colored blocks, no yellow tiles)
// ✅ ADDED: Client-side filtering for task permissions
// ✅ ADDED: Overdue column (AUTO) — tasks past due + not completed go here
// ✅ REMOVED: “Assignee must confirm completion” flow (no confirm button, no “not confirmed” state)
// ✅ KEPT: Joint tasks for end users use perUser[me] status on the board (so they can complete their own)
// ✅ KEPT: Manager Report (Admin/Owner) — who’s overdue, stale users, and “manager marked done” items
// ✅ KEPT: Attachment icon + count + expandable attachment preview (open/download)
//
// ✅ NEW: Subtask progress badge + progress bar on each card
// ✅ NEW: Subtask “sneak peek” on hover (first 3 subtasks + remainder count)

import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Loader2,
  Users,
  CalendarDays,
  Flag,
  Paperclip,
  ExternalLink,
  Download,
  AlertTriangle,
  ShieldAlert,
  Clock,
  ChevronDown,
  ChevronUp,
  ListChecks,
} from "lucide-react";
import { TaskService } from "./TaskService";
import { filterTasksForUser } from "../../utils/taskFilters";

const COLUMNS = [
  {
    id: "overdue",
    title: "Overdue",
    statusMatch: "__OVERDUE__",
    tint: "bg-red-50",
    chip: "bg-red-200 text-red-900",
    derived: true, // special column (auto)
    droppable: false,
  },
  {
    id: "todo",
    title: "To Do",
    statusMatch: "To Do",
    tint: "bg-slate-50",
    chip: "bg-slate-200 text-slate-800",
  },
  {
    id: "inprogress",
    title: "In Progress",
    statusMatch: "In Progress",
    tint: "bg-blue-50",
    chip: "bg-blue-200 text-blue-900",
  },
  {
    id: "onhold",
    title: "On Hold",
    statusMatch: "On Hold",
    tint: "bg-amber-50",
    chip: "bg-amber-200 text-amber-900",
  },
  {
    id: "done",
    title: "Done",
    statusMatch: "Completed",
    tint: "bg-emerald-50",
    chip: "bg-emerald-200 text-emerald-900",
  },
  {
    id: "cancelled",
    title: "Cancelled",
    statusMatch: "Cancelled",
    tint: "bg-rose-50",
    chip: "bg-rose-200 text-rose-900",
  },
];

const PRIORITY_DOT = {
  Low: "bg-slate-300",
  Medium: "bg-blue-400",
  High: "bg-amber-400",
  Critical: "bg-rose-500",
};

function roleLower(role) {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "");
}

function isOwnerOrAdmin(me) {
  const r = roleLower(me?.role);
  return r === "owner" || r === "admin";
}

function displayUser(u) {
  return u?.fullName || u?.name || u?.displayName || u?.email || u?.id || "User";
}

function formatDueShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function normalizeAttachment(a) {
  const name = a?.name || a?.fileName || a?.filename || a?.title || "Attachment";
  const url = a?.url || a?.downloadURL || a?.downloadUrl || a?.href || "";
  const id = a?.id || a?.attachmentId || a?.storagePath || url || name;
  return { id, name, url };
}

// Looks for attachment arrays already present on the task (optional)
function getInlineAttachments(task) {
  const arr =
    (Array.isArray(task?.attachments) && task.attachments) ||
    (Array.isArray(task?.attachmentsPreview) && task.attachmentsPreview) ||
    (Array.isArray(task?.files) && task.files) ||
    (Array.isArray(task?.attachmentFiles) && task.attachmentFiles) ||
    [];
  return arr.map(normalizeAttachment).filter((x) => x.name || x.url);
}

function openUrl(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function downloadUrl(url, filename) {
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function toMillisAny(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (typeof v?.toDate === "function") return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  return 0;
}

function isTaskCancelled(task) {
  return (task?.status || "") === "Cancelled";
}

function getMyUid(me) {
  return me?.uid || me?.id || null;
}

// For joint tasks: board should reflect MY status for end users (so they can complete their part)
function getBoardStatus(task, me, isAdminOrOwner) {
  const base = task?.status || "To Do";
  if (isAdminOrOwner) return base;

  const myUid = getMyUid(me);
  if (!myUid) return base;

  if (task?.distribution === "joint" && task?.perUser && task.perUser[myUid]?.status) {
    return task.perUser[myUid].status || "To Do";
  }
  return base;
}

function getAssigneeIds(task) {
  const a = Array.isArray(task?.assignedToUserIds) ? task.assignedToUserIds.filter(Boolean) : [];
  const m = Array.isArray(task?.memberIds) ? task.memberIds.filter(Boolean) : [];
  // Prefer explicit memberIds for joint
  if ((task?.distribution === "joint" || task?.assignMode === "group") && m.length) return m;
  return a;
}

// ✅ SIMPLE completion logic (NO confirmation concept):
// - Cancelled is treated as “closed”
// - Joint: all members must have perUser[uid].status === "Completed" (each person completes their part)
// - Otherwise: status === "Completed"
function isTrulyCompleted(task) {
  if (!task) return false;
  if (isTaskCancelled(task)) return true;

  if (
    task?.distribution === "joint" &&
    task?.perUser &&
    Array.isArray(task?.memberIds) &&
    task.memberIds.length
  ) {
    return task.memberIds.every((uid) => task.perUser?.[uid]?.status === "Completed");
  }

  return (task?.status || "To Do") === "Completed";
}

function isOverdue(task, nowMillis) {
  if (!task?.dueAt) return false;
  if (isTaskCancelled(task)) return false;

  const dueMillis = toMillisAny(task.dueAt);
  if (!dueMillis) return false;

  return dueMillis < nowMillis && !isTrulyCompleted(task);
}

function overdueDays(task, nowMillis) {
  const dueMillis = toMillisAny(task?.dueAt);
  if (!dueMillis) return 0;
  const diff = nowMillis - dueMillis;
  return diff > 0 ? Math.floor(diff / 86400000) : 0;
}

// ✅ “Manager marked done” (informational only; no assignee confirmation needed):
function isManagerMarkedDone(task) {
  if (!task) return false;
  if (isTaskCancelled(task)) return false;

  const status = task?.status || "To Do";
  if (status !== "Completed") return false;

  // Joint tasks: this concept isn’t useful (completion is perUser-driven)
  if (task?.distribution === "joint") return false;

  const assignees = getAssigneeIds(task);
  if (!assignees.length) return false;

  const actorUid = task?.completedByUid || task?.updatedByUid || null;
  const actorRole = roleLower(task?.completedByRole || task?.updatedByRole || "");

  const isMgr = actorRole === "owner" || actorRole === "admin";
  if (!isMgr) return false;

  // If manager completed it *as an assignee*, it’s not “manager marked”
  if (actorUid && assignees.includes(actorUid)) return false;

  return true;
}

// ✅ NEW: Normalize subtasks (supports task.subtasks + fallbacks)
function getSubtaskList(task) {
  const raw =
    (Array.isArray(task?.subtasks) && task.subtasks) ||
    (Array.isArray(task?.checklist) && task.checklist) ||
    (Array.isArray(task?.checklistItems) && task.checklistItems) ||
    [];

  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((s, idx) => ({
      id: s?.id || s?.subtaskId || `${idx}`,
      title: (s?.title || s?.text || s?.name || "").toString(),
      isDone: !!(s?.isDone ?? s?.done ?? s?.completed),
    }))
    .filter((s) => (s.title || "").trim());
}

function getSubtaskStats(task) {
  const list = getSubtaskList(task);
  const total = list.length;
  const done = list.filter((s) => s.isDone).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return { total, done, pct, list };
}

export default function TaskKanban({
  companyId,
  me,
  usersMap,
  groupsMap,
  onEdit,
  onRefresh,
  showManagerReport = true,
}) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [rawTasks, setRawTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragged, setDragged] = useState(null);
  const [error, setError] = useState("");

  const isAdminOrOwner = isOwnerOrAdmin(me);
  const myUid = getMyUid(me);

  // ✅ Attachment UI state
  const [expandedAtt, setExpandedAtt] = useState({}); // { [taskId]: true/false }
  const [attCache, setAttCache] = useState({}); // { [taskId]: Attachment[] }
  const [attLoading, setAttLoading] = useState({}); // { [taskId]: true/false }

  // ✅ Manager report UI
  const [showManagerDetails, setShowManagerDetails] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ CLIENT-SIDE FILTERING: Only show tasks user has permission to see
  const tasks = useMemo(() => {
    return filterTasksForUser(rawTasks, me);
  }, [rawTasks, me]);

  const load = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError("");
      const all = await TaskService.getCompanyTasks(companyId, { me });
      setRawTasks(Array.isArray(all) ? all : []);
    } catch (e) {
      console.error("[TaskKanban] load error:", e);
      setError("Failed to load tasks.");
      setRawTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, me?.uid]);

  const assigneesLabel = (task) => {
    if (task.assignMode === "group") {
      const g =
        task.groupName ||
        groupsMap?.[task.groupId]?.name ||
        groupsMap?.[task.groupId]?.title ||
        "";
      return g ? `Group: ${g}` : "Group task";
    }
    const ids = Array.isArray(task.assignedToUserIds) ? task.assignedToUserIds : [];
    if (!ids.length) return "Unassigned";
    return (
      ids
        .slice(0, 2)
        .map((uid) => displayUser(usersMap?.[uid]) || uid)
        .join(", ") + (ids.length > 2 ? ` +${ids.length - 2}` : "")
    );
  };

  // ✅ Build columns:
  // - Overdue column is derived (auto)
  // - Other columns use "board status" (joint tasks use perUser status for end users)
  const byColumnId = useMemo(() => {
    const nowMillis = Date.now();

    const map = new Map();
    COLUMNS.forEach((c) => map.set(c.id, []));

    tasks.forEach((t) => {
      if (isOverdue(t, nowMillis)) {
        map.get("overdue").push(t);
        return;
      }

      const s = getBoardStatus(t, me, isAdminOrOwner);
      const col =
        COLUMNS.find((c) => !c.derived && c.statusMatch === s) ||
        COLUMNS.find((c) => c.id === "todo");
      map.get(col.id).push(t);
    });

    // Sort Overdue by oldest due first (worst offenders float up)
    const overdueArr = map.get("overdue") || [];
    overdueArr.sort((a, b) => {
      const ad = toMillisAny(a.dueAt) || Number.POSITIVE_INFINITY;
      const bd = toMillisAny(b.dueAt) || Number.POSITIVE_INFINITY;
      return ad - bd;
    });
    map.set("overdue", overdueArr);

    // Sort other columns by due date
    COLUMNS.filter((c) => !c.derived).forEach((c) => {
      const arr = map.get(c.id) || [];
      arr.sort((a, b) => {
        const ad = a.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY;
        const bd = b.dueAt ? new Date(b.dueAt).getTime() : Number.POSITIVE_INFINITY;
        return ad - bd;
      });
      map.set(c.id, arr);
    });

    return map;
  }, [tasks, me, isAdminOrOwner]);

  // ✅ Manager report (Admin/Owner only)
  const managerReport = useMemo(() => {
    if (!isAdminOrOwner) return null;

    const nowMillis = Date.now();
    const overdueTasks = tasks.filter((t) => isOverdue(t, nowMillis));
    const managerDoneTasks = tasks.filter((t) => isManagerMarkedDone(t));

    const perUser = new Map();
    const ensure = (uid) => {
      const key = uid || "__unassigned__";
      if (!perUser.has(key)) {
        perUser.set(key, {
          uid: key,
          overdue: 0,
          staleOverdue: 0,
          managerMarkedDone: 0,
          oldestOverdueDays: 0,
          lastTouchMillis: 0,
          sampleTasks: [],
        });
      }
      return perUser.get(key);
    };

    const addTouch = (uid, millis) => {
      const s = ensure(uid);
      s.lastTouchMillis = Math.max(s.lastTouchMillis || 0, millis || 0);
    };

    const addOldest = (uid, days) => {
      const s = ensure(uid);
      s.oldestOverdueDays = Math.max(s.oldestOverdueDays || 0, days || 0);
    };

    const accountableIdsForOverdue = (task) => {
      const ids = getAssigneeIds(task);
      if (!ids.length) return ["__unassigned__"];

      if (
        task?.distribution === "joint" &&
        task?.perUser &&
        Array.isArray(task?.memberIds) &&
        task.memberIds.length
      ) {
        const open = task.memberIds.filter((uid) => task.perUser?.[uid]?.status !== "Completed");
        return open.length ? open : ids;
      }
      return ids;
    };

    overdueTasks.forEach((t) => {
      const dueMillis = toMillisAny(t.dueAt);
      const days = overdueDays(t, nowMillis);
      const touchMillis = Math.max(toMillisAny(t.updatedAt), dueMillis);

      const ids = accountableIdsForOverdue(t);
      ids.forEach((uid) => {
        const s = ensure(uid);
        s.overdue += 1;
        addOldest(uid, days);
        addTouch(uid, touchMillis);

        const lastTouch = toMillisAny(t.updatedAt) || 0;
        const staleDays = lastTouch ? Math.floor((nowMillis - lastTouch) / 86400000) : 999;
        if (staleDays >= 7) s.staleOverdue += 1;

        if (s.sampleTasks.length < 5) s.sampleTasks.push(t);
      });
    });

    managerDoneTasks.forEach((t) => {
      const ids = getAssigneeIds(t);
      (ids.length ? ids : ["__unassigned__"]).forEach((uid) => {
        const s = ensure(uid);
        s.managerMarkedDone += 1;
        addTouch(uid, toMillisAny(t.updatedAt));
      });
    });

    const rows = Array.from(perUser.values()).filter(
      (r) => r.overdue > 0 || r.managerMarkedDone > 0
    );

    rows.sort((a, b) => {
      if (b.overdue !== a.overdue) return b.overdue - a.overdue;
      if (b.staleOverdue !== a.staleOverdue) return b.staleOverdue - a.staleOverdue;
      return (b.oldestOverdueDays || 0) - (a.oldestOverdueDays || 0);
    });

    const formatLastTouch = (millis) => {
      if (!millis) return "—";
      const days = Math.floor((nowMillis - millis) / 86400000);
      if (days <= 0) return "today";
      if (days === 1) return "1 day ago";
      return `${days} days ago`;
    };

    const totals = {
      total: tasks.length,
      overdue: overdueTasks.length,
      staleOverdue: overdueTasks.filter((t) => {
        const last = toMillisAny(t.updatedAt) || 0;
        const days = last ? Math.floor((nowMillis - last) / 86400000) : 999;
        return days >= 7;
      }).length,
      managerMarkedDone: managerDoneTasks.length,
    };

    return { rows, totals, formatLastTouch, overdueTasks, managerDoneTasks };
  }, [isAdminOrOwner, tasks]);

  const handleDragStart = (e, task) => {
    setDragged(task);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", task.id);
    } catch (_) {}
  };

  const handleDragOver = (e, col) => {
    if (col?.derived && col?.droppable === false) return;
    e.preventDefault();
    try {
      e.dataTransfer.dropEffect = "move";
    } catch (_) {}
  };

  const handleDrop = async (e, colId) => {
    e.preventDefault();
    const col = COLUMNS.find((c) => c.id === colId);
    if (!col) return;

    if (col.derived && col.droppable === false) {
      setDragged(null);
      return;
    }

    const newStatus = col.statusMatch;
    const t = dragged;

    if (!t?.id) return;

    const currentBoardStatus = getBoardStatus(t, me, isAdminOrOwner);
    if (currentBoardStatus === newStatus) {
      setDragged(null);
      return;
    }

    setRawTasks((prev) =>
      prev.map((x) => {
        if (x.id !== t.id) return x;

        if (!isAdminOrOwner && x?.distribution === "joint" && myUid) {
          const perUser = { ...(x.perUser || {}) };
          perUser[myUid] = { ...(perUser[myUid] || {}), status: newStatus };
          return { ...x, perUser };
        }

        return { ...x, status: newStatus };
      })
    );

    try {
      if (!isAdminOrOwner && t?.distribution === "joint" && myUid) {
        await TaskService.updateMyJointStatus(companyId, t.id, myUid, newStatus, me?.role);
      } else {
        await TaskService.updateTask(companyId, t.id, {
          status: newStatus,
          updatedByRole: me?.role,
        });
      }

      onRefresh?.();
    } catch (err) {
      console.error("[TaskKanban] drop update failed:", err);
      await load();
      setError("Failed to move task. Please try again.");
    } finally {
      setDragged(null);
    }
  };

  const toggleAttachments = async (e, task) => {
    e.preventDefault();
    e.stopPropagation();

    if (!task?.id) return;
    const taskId = task.id;

    setExpandedAtt((prev) => ({ ...prev, [taskId]: !prev[taskId] }));

    const goingOpen = !expandedAtt[taskId];
    if (!goingOpen) return;

    if (Array.isArray(attCache[taskId]) && attCache[taskId].length) return;

    const inline = getInlineAttachments(task);
    if (inline.length) {
      setAttCache((prev) => ({ ...prev, [taskId]: inline }));
      return;
    }

    if (typeof TaskService.getTaskAttachments !== "function") return;

    try {
      setAttLoading((prev) => ({ ...prev, [taskId]: true }));
      const found = await TaskService.getTaskAttachments(companyId, taskId, { me });
      const normalized = (Array.isArray(found) ? found : [])
        .map(normalizeAttachment)
        .filter((x) => x.name || x.url);
      setAttCache((prev) => ({ ...prev, [taskId]: normalized }));
    } catch (err) {
      console.error("[TaskKanban] getTaskAttachments failed:", err);
    } finally {
      setAttLoading((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm font-medium">Loading board…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-red-800">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ✅ Manager Report (Admin/Owner only) */}
      {showManagerReport && isAdminOrOwner && managerReport ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">Manager Report</div>
                <div className="text-xs text-slate-500">
                  Overdue accountability • stale users • manager marked done
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowManagerDetails((p) => !p)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium"
            >
              {showManagerDetails ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              {showManagerDetails ? "Hide details" : "Show details"}
            </button>
          </div>

          <div className="px-4 py-3">
            <div className="flex flex-wrap gap-2 text-[11px]">
              <div className="px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-700">
                Total tasks: <span className="font-semibold">{managerReport.totals.total}</span>
              </div>
              <div className="px-2 py-1 rounded-full border border-red-200 bg-red-50 text-red-700 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                Overdue: <span className="font-semibold">{managerReport.totals.overdue}</span>
              </div>
              <div className="px-2 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800 inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                Stale (7d+): <span className="font-semibold">{managerReport.totals.staleOverdue}</span>
              </div>
              <div className="px-2 py-1 rounded-full border border-slate-200 bg-white text-slate-700 inline-flex items-center gap-1">
                <ShieldAlert className="w-3.5 h-3.5" />
                Mgr done: <span className="font-semibold">{managerReport.totals.managerMarkedDone}</span>
              </div>
            </div>

            {showManagerDetails ? (
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top offenders */}
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-800">
                    Who’s overdue (by person)
                  </div>

                  {managerReport.rows.length === 0 ? (
                    <div className="p-3 text-xs text-slate-500">No overdue items 🎉</div>
                  ) : (
                    <div className="divide-y divide-slate-200">
                      {managerReport.rows.slice(0, 10).map((r) => {
                        const u = r.uid === "__unassigned__" ? null : usersMap?.[r.uid];
                        const name =
                          r.uid === "__unassigned__" ? "Unassigned" : displayUser(u) || r.uid;

                        return (
                          <div key={r.uid} className="px-3 py-2 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-900 truncate">{name}</div>
                              <div className="text-[11px] text-slate-500">
                                Last touch: {managerReport.formatLastTouch(r.lastTouchMillis)}
                                {r.oldestOverdueDays ? ` • Oldest overdue: ${r.oldestOverdueDays}d` : ""}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 text-[11px] flex-shrink-0">
                              <span className="px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-700">
                                Overdue <span className="font-semibold">{r.overdue}</span>
                              </span>
                              {r.staleOverdue ? (
                                <span className="px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
                                  Stale <span className="font-semibold">{r.staleOverdue}</span>
                                </span>
                              ) : null}
                              {r.managerMarkedDone ? (
                                <span className="px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                                  Mgr done <span className="font-semibold">{r.managerMarkedDone}</span>
                                </span>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Overdue tasks */}
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-800">
                    Overdue tasks (click to open)
                  </div>

                  {managerReport.overdueTasks.length === 0 ? (
                    <div className="p-3 text-xs text-slate-500">No overdue tasks.</div>
                  ) : (
                    <div className="divide-y divide-slate-200 max-h-[320px] overflow-y-auto">
                      {managerReport.overdueTasks.slice(0, 25).map((t) => {
                        const due = formatDueShort(t.dueAt);
                        const days = overdueDays(t, Date.now());
                        const assign = assigneesLabel(t);

                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => onEdit?.(t)}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-900 truncate">
                                  {t.title || "Untitled Task"}
                                </div>
                                <div className="text-[11px] text-slate-600 truncate">
                                  {assign} {due ? `• Due ${due}` : ""} {days ? `• ${days}d overdue` : ""}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="px-2 py-1 rounded-full bg-red-50 border border-red-200 text-[10px] text-red-700">
                                  Overdue
                                </span>
                              </div>
                            </div>
                          </button>
                        );
                      })}

                      {managerReport.overdueTasks.length > 25 ? (
                        <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50">
                          + {managerReport.overdueTasks.length - 25} more…
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Manager-marked done tasks */}
                <div className="border border-slate-200 rounded-xl overflow-hidden lg:col-span-2">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-800">
                    Manager marked done (click to open)
                  </div>

                  {managerReport.managerDoneTasks.length === 0 ? (
                    <div className="p-3 text-xs text-slate-500">No manager-marked items.</div>
                  ) : (
                    <div className="divide-y divide-slate-200 max-h-[260px] overflow-y-auto">
                      {managerReport.managerDoneTasks.slice(0, 25).map((t) => {
                        const due = formatDueShort(t.dueAt);
                        const assign = assigneesLabel(t);

                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => onEdit?.(t)}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-900 truncate">
                                  {t.title || "Untitled Task"}
                                </div>
                                <div className="text-[11px] text-slate-600 truncate">
                                  {assign} {due ? `• Due ${due}` : ""}
                                </div>
                              </div>

                              <span className="px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-[10px] text-slate-700">
                                Mgr done
                              </span>
                            </div>
                          </button>
                        );
                      })}

                      {managerReport.managerDoneTasks.length > 25 ? (
                        <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50">
                          + {managerReport.managerDoneTasks.length - 25} more…
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Board */}
      <div className={`${isMobile ? "" : "overflow-x-auto pb-2"}`}>
        <div className={`flex gap-3 ${isMobile ? "flex-col" : "min-w-max"}`}>
          {COLUMNS.map((col) => {
            const colTasks = byColumnId.get(col.id) || [];
            return (
              <div
                key={col.id}
                className={`${isMobile ? "w-full" : "w-[300px]"} rounded-2xl border border-slate-200 ${col.tint}`}
                onDragOver={(e) => handleDragOver(e, col)}
                onDrop={(e) => handleDrop(e, col.id)}
              >
                {/* Column header */}
                <div className="px-3 py-3 border-b border-slate-200 bg-white/70 rounded-t-2xl flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">{col.title}</div>
                  <div className={`text-xs font-semibold px-2 py-0.5 rounded-full ${col.chip}`}>
                    {colTasks.length}
                  </div>
                </div>

                {/* Column body */}
                <div className={`${isMobile ? "p-2" : "p-3"} space-y-2`}>
                  {colTasks.length === 0 ? (
                    <div className="text-xs text-slate-500 text-center py-8">
                      {col.derived ? "No overdue tasks" : "Drop tasks here"}
                    </div>
                  ) : (
                    colTasks.map((t) => {
                      const dot = PRIORITY_DOT[t.priority] || "bg-slate-300";
                      const due = formatDueShort(t.dueAt);
                      const assign = assigneesLabel(t);

                      const nowMillis = Date.now();
                      const overdue = isOverdue(t, nowMillis);
                      const daysLate = overdue ? overdueDays(t, nowMillis) : 0;

                      const managerDone = isManagerMarkedDone(t);

                      const inlineAtt = getInlineAttachments(t);
                      const cachedAtt = Array.isArray(attCache[t.id]) ? attCache[t.id] : [];
                      const allAtt = cachedAtt.length ? cachedAtt : inlineAtt;

                      const attCount = Number(
                        t.attachmentsCount ??
                          t.attachmentCount ??
                          t.attachments_count ??
                          (Array.isArray(t.attachments) ? t.attachments.length : 0) ??
                          allAtt.length ??
                          0
                      );

                      const isExpanded = !!expandedAtt[t.id];
                      const preview = (allAtt || []).slice(0, 2);
                      const remaining = Math.max(0, (allAtt || []).length - 2);
                      const isAttBusy = !!attLoading[t.id];

                      // ✅ Subtasks stats + hover preview
                      const st = getSubtaskStats(t);
                      const showSubtasks = st.total > 0;
                      const peek = showSubtasks ? st.list.slice(0, 3) : [];
                      const peekRemaining = showSubtasks ? Math.max(0, st.list.length - 3) : 0;

                      // Card styling: red while overdue (until completed)
                      const cardClass = overdue
                        ? "bg-red-50 border-red-200 hover:bg-red-100"
                        : "bg-white border-slate-200 hover:shadow-md";

                      return (
                        <div
                          key={t.id}
                          draggable={!(col.derived && col.droppable === false)}
                          onDragStart={(e) => handleDragStart(e, t)}
                          onClick={() => onEdit?.(t)}
                          className={`group relative rounded-xl border shadow-sm transition cursor-pointer ${isMobile ? "p-2.5" : "p-3"} ${
                            dragged?.id === t.id ? "opacity-60" : ""
                          } ${cardClass}`}
                          title="Click to edit"
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${dot}`} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-slate-900 truncate">
                                    {t.title || "Untitled Task"}
                                  </div>
                                </div>

                                {/* Info chips */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {overdue ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-red-100 border border-red-200 text-red-800">
                                      <AlertTriangle className="w-3 h-3" />
                                      {daysLate ? `${daysLate}d` : "Overdue"}
                                    </span>
                                  ) : null}

                                  {managerDone ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-slate-100 border border-slate-200 text-slate-700">
                                      <ShieldAlert className="w-3 h-3" />
                                      Mgr done
                                    </span>
                                  ) : null}
                                </div>
                              </div>

                              {t.description ? (
                                <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                                  {t.description}
                                </div>
                              ) : null}

                              {/* Meta row */}
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-700">
                                <span className="inline-flex items-center gap-1">
                                  <Users className="w-3.5 h-3.5 text-slate-500" />
                                  <span className="truncate max-w-[210px]">{assign}</span>
                                </span>

                                {due ? (
                                  <span className="inline-flex items-center gap-1">
                                    <CalendarDays className="w-3.5 h-3.5 text-slate-500" />
                                    {due}
                                  </span>
                                ) : null}

                                {t.type ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Flag className="w-3.5 h-3.5 text-slate-500" />
                                    {t.type}
                                  </span>
                                ) : null}

                                {t.distribution === "joint" ? (
                                  <span className="px-2 py-0.5 rounded-full border border-slate-200 bg-white/70 text-slate-700">
                                    Shared
                                  </span>
                                ) : null}

                                {/* Subtask progress chip */}
                                {showSubtasks ? (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 bg-white/70 text-slate-700"
                                    title="Subtasks completed"
                                  >
                                    <ListChecks className="w-3.5 h-3.5 text-slate-500" />
                                    <span className="font-medium">
                                      {st.done}/{st.total}
                                    </span>
                                  </span>
                                ) : null}

                                {/* Attachments icon + count */}
                                {attCount > 0 ? (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                                    title={isExpanded ? "Hide attachments" : "Show attachments"}
                                    onClick={(e) => toggleAttachments(e, t)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onDragStart={(e) => e.stopPropagation()}
                                    draggable={false}
                                  >
                                    <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                                    <span className="font-medium">{attCount}</span>
                                  </button>
                                ) : null}
                              </div>

                              {/* Subtask progress bar (subtle) */}
                              {showSubtasks ? (
                                <div className="mt-2">
                                  <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                                    <div className="h-full bg-emerald-500" style={{ width: `${st.pct}%` }} />
                                  </div>
                                </div>
                              ) : null}

                              {/* ✅ NEW: Hover “sneak peek” (non-blocking) */}
                              {showSubtasks ? (
                                <div className="pointer-events-none mt-2 overflow-hidden opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-24 transition-all duration-200">
                                  <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
                                    <ListChecks className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Subtasks</span>
                                  </div>

                                  <div className="mt-1 space-y-1">
                                    {peek.map((s) => (
                                      <div key={s.id} className="flex items-center gap-2 text-[11px] text-slate-700">
                                        <span
                                          className={`inline-flex items-center justify-center w-4 h-4 rounded-md border ${
                                            s.isDone
                                              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                              : "bg-white border-slate-200 text-slate-400"
                                          }`}
                                        >
                                          {s.isDone ? "✓" : "•"}
                                        </span>
                                        <span className="truncate">{s.title}</span>
                                      </div>
                                    ))}

                                    {peekRemaining > 0 ? (
                                      <div className="text-[11px] text-slate-500">+{peekRemaining} more…</div>
                                    ) : null}
                                  </div>
                                </div>
                              ) : null}

                              {/* Expanded attachment preview */}
                              {isExpanded ? (
                                <div
                                  className="mt-2 space-y-1"
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
                                    <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Attachments</span>
                                    {isAttBusy ? (
                                      <span className="inline-flex items-center gap-1 text-slate-500 font-normal">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        Loading…
                                      </span>
                                    ) : null}
                                  </div>

                                  {!isAttBusy && preview.length === 0 ? (
                                    <div className="text-xs text-slate-500">
                                      Attachment details aren’t available here yet.
                                    </div>
                                  ) : null}

                                  {preview.map((a) => (
                                    <div key={a.id} className="flex items-center justify-between gap-2 py-1">
                                      <div className="min-w-0">
                                        <div className="text-xs text-slate-700 truncate">{a.name}</div>
                                      </div>

                                      <div className="flex items-center gap-0.5 flex-shrink-0">
                                        <button
                                          type="button"
                                          className={`p-1 rounded-md hover:bg-slate-100 transition ${
                                            a.url ? "" : "opacity-40 cursor-not-allowed"
                                          }`}
                                          title="Open"
                                          disabled={!a.url}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            openUrl(a.url);
                                          }}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          onDragStart={(e) => e.stopPropagation()}
                                          draggable={false}
                                        >
                                          <ExternalLink className="w-4 h-4 text-slate-700" />
                                        </button>

                                        <button
                                          type="button"
                                          className={`p-1 rounded-md hover:bg-slate-100 transition ${
                                            a.url ? "" : "opacity-40 cursor-not-allowed"
                                          }`}
                                          title="Download"
                                          disabled={!a.url}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            downloadUrl(a.url, a.name);
                                          }}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          onDragStart={(e) => e.stopPropagation()}
                                          draggable={false}
                                        >
                                          <Download className="w-4 h-4 text-slate-700" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}

                                  {!isAttBusy && remaining > 0 ? (
                                    <div className="text-[11px] text-slate-500 leading-tight">
                                      +{remaining} more
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tiny explanation for overdue behavior */}
      <div className="text-[11px] text-slate-500 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-red-600" />
        Overdue is automatic: due date passed + not completed.
      </div>
    </div>
  );
}
